// ─── session-client-core.js ──────────────────────────────────────────────────
// React-free classic script (node-unit-testable, the same pattern as
// session-expiry-core.js and sign-in-window-core.js). It owns the PAGE's half of
// the sign-in service (plan 27, approved by Omar on 2026-10-16).
//
// ── What changed, and why ────────────────────────────────────────────────────
// Omar, 2026-10-16: "Renewal card storage: implement it now." Until then Auth0's
// library ran in this page and kept its cache, refresh token included, in
// localStorage, where any script on the site could read it. Now our server keeps
// the card in a protected cookie this page cannot read, and this file only ever
// holds the 1-hour pass, in memory. A reload simply asks for a new one.
//
// ── The four questions it asks our server (same origin, through the relay) ──
//   GET  /auth/session   signed in? until when? — renews NOTHING (the minute check)
//   POST /auth/refresh   a fresh pass — renews the card, which counts as activity
//   POST /auth/logout    cancel the card and clear the cookie
// The sign-in itself happens in the sign-in window (sign-in-window-core.js).
//
// ★ Answers are kept apart as the session-expiry rules need them: a 401 means the
//   session ENDED (`error: 'session_ended' | 'signed_out'`); anything else — the
//   network, a 503 — is `error: 'unavailable'`, which must never sign anyone out.
(function () {
  'use strict';

  var SESSION_URL = '/auth/session';
  var REFRESH_URL = '/auth/refresh';
  var LOGOUT_URL = '/auth/logout';
  /** The header our server requires: a page on another site cannot send it. */
  var AUTH_HEADER = 'x-dynamo-auth';
  /**
   * When this session's two windows end, shared by every tab of the site. Not a
   * secret: two times. It is what lets the once-a-minute check know an end has
   * passed without asking anyone (Omar's 164).
   */
  var ENDS_KEY = 'dynamo.session.ends';
  /** One renewal at a time across tabs: with rotation, a card sent twice cancels them all. */
  var RENEW_LOCK = 'dynamo-auth-renew';
  /** Renew a pass a minute before it runs out, so no request leaves with a dying one. */
  var EARLY_MS = 60000;
  /** How long a sign-out lets a renewal already on its way land first, at most. */
  var SIGN_OUT_WAIT_MS = 5000;

  function failure(code, message) {
    var e = new Error(message || code);
    e.error = code;
    return e;
  }

  /** Has one of the session's two windows ended, by this clock? Unknown means "ask". */
  function endHasPassed(ends, now) {
    if (!ends || !isFinite(ends.idleEndsAt) || !isFinite(ends.maxEndsAt)) return true;
    return now >= Math.min(ends.idleEndsAt, ends.maxEndsAt);
  }

  /**
   * o = { win, fetchFn: (url, init) => Promise<Response>, now?: () => number }
   */
  function createSessionClient(o) {
    var win = o.win;
    var fetchFn = o.fetchFn;
    var now = o.now || function () { return Date.now(); };
    var pass = null; // { token, expiresAt }
    var user = null; // { email, name }
    var inflight = null;
    var lastEnds = null;
    // ★ Signing out: from the moment it starts until the page has gone, nothing is renewed,
    //   nothing is written to this browser and nothing announces an ending. The page lives on
    //   for a round trip to Auth0's sign-out, and a sign-out clears this browser (Omar,
    //   2026-10-07): a timer or a request in that window must not put anything back.
    //   Cleared again if the sign-out fails, because then the person is still signed in.
    var closing = false;
    // ★ Moves on each time this tab forgets the session. An answer to a question asked
    //   before that belongs to the session that is over, so it is not kept: the question is
    //   asked again, once, and our server — which knows the cookie is gone — answers what is
    //   true now. Without it, a renewal on its way when another tab signed out put the pass,
    //   the person and the end times back (review of plan 27, round 2, R2).
    var generation = 0;

    function storage() {
      try { return win.localStorage; } catch (e) { return null; }
    }
    /**
     * Drop what this tab holds of a session: the pass, the person, the end times it
     * knew. Memory only — what other tabs read is left to whoever ended the session.
     */
    function forget() {
      generation += 1;
      pass = null;
      user = null;
      lastEnds = null;
    }
    // ★ Another tab signed out (it empties this browser's storage) or saw the session
    //   end (it removes the end times): the cookie is gone for every tab, so the pass
    //   this tab holds belongs to a session that is over. Kept, it would go on working
    //   for up to an hour — and, after a sign-in in this tab, as the PREVIOUS person
    //   (review of plan 27; the Console's tabs follow this way too, at their next request).
    if (win && typeof win.addEventListener === 'function') {
      win.addEventListener('storage', function (ev) {
        if (!ev) return;
        var cleared = ev.key === null || ev.key === undefined;
        var endsGone = ev.key === ENDS_KEY && (ev.newValue === null || ev.newValue === undefined);
        if (cleared || endsGone) forget();
      });
    }
    function rememberEnds(body) {
      if (closing) return;
      if (!body || typeof body.idle_ends_in !== 'number' || typeof body.max_ends_in !== 'number') return;
      var t = now();
      lastEnds = { idleEndsAt: t + body.idle_ends_in * 1000, maxEndsAt: t + body.max_ends_in * 1000 };
      var s = storage();
      if (s) {
        try { s.setItem(ENDS_KEY, JSON.stringify(lastEnds)); } catch (e) { /* the copy in memory still serves this tab */ }
      }
    }
    function forgetEnds() {
      lastEnds = null;
      var s = storage();
      if (s) {
        try { s.removeItem(ENDS_KEY); } catch (e) { /* nothing to forget */ }
      }
    }
    /** The latest end times any tab learned, or this tab's own. */
    function ends() {
      var s = storage();
      if (s) {
        try {
          var raw = s.getItem(ENDS_KEY);
          var v = raw ? JSON.parse(raw) : null;
          if (v && isFinite(v.idleEndsAt) && isFinite(v.maxEndsAt)) return v;
        } catch (e) { /* fall back to this tab's copy */ }
      }
      return lastEnds;
    }

    async function call(method, path) {
      var res;
      try {
        var headers = {};
        headers[AUTH_HEADER] = '1';
        res = await fetchFn(path, { method: method, headers: headers, credentials: 'same-origin', cache: 'no-store' });
      } catch (e) {
        throw failure('unavailable', 'the sign-in service did not answer: ' + ((e && e.message) || e));
      }
      var body = null;
      try { body = await res.json(); } catch (e) { body = null; }
      return { status: res.status, body: body || {} };
    }

    /** Signed in, and until when. Asks our server's clock; renews nothing. */
    async function status(again) {
      if (closing) throw failure('unavailable', 'signing out');
      var asked = generation;
      var r = await call('GET', SESSION_URL);
      if (r.status !== 200) throw failure('unavailable', 'the session check answered ' + r.status);
      if (asked !== generation) {
        if (again) throw failure('unavailable', 'the session changed while it was being checked');
        return status(true);
      }
      if (r.body.signedIn) {
        user = { email: r.body.email || '', name: r.body.name || '' };
        rememberEnds(r.body);
      } else {
        pass = null;
        forgetEnds();
      }
      return { signedIn: !!r.body.signedIn, reason: r.body.reason || null };
    }

    async function renewNow(again) {
      if (closing) throw failure('unavailable', 'signing out');
      var asked = generation;
      var r = await call('POST', REFRESH_URL);
      if (asked !== generation) {
        if (again) throw failure('unavailable', 'the session changed while it was being renewed');
        return renewNow(true);
      }
      if (r.status === 200 && typeof r.body.access_token === 'string') {
        pass = { token: r.body.access_token, expiresAt: now() + (Number(r.body.expires_in) || 0) * 1000 };
        user = { email: r.body.email || '', name: r.body.name || '' };
        rememberEnds(r.body);
        return pass.token;
      }
      if (r.status === 401) {
        pass = null;
        forgetEnds();
        throw failure(r.body.code === 'signed_out' ? 'signed_out' : 'session_ended', r.body.message || 'Your session ended.');
      }
      throw failure('unavailable', 'the renewal answered ' + r.status);
    }

    /** A fresh pass now, renewing the card. One at a time, in this tab and across tabs. */
    function renew() {
      if (inflight) return inflight;
      var locks = win.navigator && win.navigator.locks;
      var run = locks && typeof locks.request === 'function'
        ? locks.request(RENEW_LOCK, function () { return renewNow(); })
        : renewNow();
      inflight = Promise.resolve(run).finally(function () { inflight = null; });
      return inflight;
    }

    /** The pass for a request: the one in memory while it lasts, otherwise a fresh one. */
    async function getPass() {
      if (pass && pass.expiresAt - EARLY_MS > now()) return pass.token;
      return renew();
    }

    /** Cancel the card and clear the cookie. Resolves with Auth0's own sign-out address, or null. */
    async function signOut() {
      closing = true;
      // ★ A renewal already on its way comes back with a NEW card in the cookie. Let it
      //   land first, so the sign-out cancels the card this browser actually holds
      //   (review of plan 27, S3; our server also refuses to revive a signed-out card).
      //   Bounded: a renewal that never answers must not hold the sign-out hostage.
      if (inflight) {
        var timer = null;
        await Promise.race([
          inflight.then(function () {}, function () {}),
          new Promise(function (resolve) { timer = setTimeout(resolve, SIGN_OUT_WAIT_MS); }),
        ]);
        if (timer !== null) clearTimeout(timer);
      }
      forget();
      forgetEnds();
      try {
        var r = await call('POST', LOGOUT_URL);
        if (r.status !== 200) throw failure('unavailable', 'the sign-out answered ' + r.status);
        return { logoutUrl: typeof r.body.logoutUrl === 'string' ? r.body.logoutUrl : null };
      } catch (e) {
        closing = false;
        throw e;
      }
    }

    return {
      // Wrapped: the retry flag is this file's own, never a caller's argument.
      status: function () { return status(); },
      renew: renew,
      getPass: getPass,
      ends: ends,
      signOut: signOut,
      forget: forget,
      user: function () { return user; },
    };
  }

  var api = {
    SESSION_URL: SESSION_URL,
    REFRESH_URL: REFRESH_URL,
    LOGOUT_URL: LOGOUT_URL,
    AUTH_HEADER: AUTH_HEADER,
    ENDS_KEY: ENDS_KEY,
    createSessionClient: createSessionClient,
    endHasPassed: endHasPassed,
  };

  if (typeof window !== 'undefined') {
    window.SessionClientCore = api;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})();
